import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Building2, Users, ShieldCheck, FileSignature, Receipt, Megaphone, KeyRound, BadgeCheck, CalendarPlus } from "lucide-react";
import { useSeo } from "../lib/useSeo";
import { buildIcs, downloadIcs } from "../lib/ics";

const API = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  [Users, "Pro access for your whole team", "Every employee gets full Pro membership — the complete short-command dictionary, every category, every industry pack."],
  [KeyRound, "Bulk onboarding in minutes", "Send us a simple list of names, job titles and emails. We create every account with secure temporary passwords — staff set their own on first login."],
  [ShieldCheck, "NDA-protected discussions", "Before any commercial details are shared, we issue a mutual NDA you can e-sign online in under a minute. No printing, no scanning."],
  [Receipt, "Professional invoicing", "VAT-ready invoices from Voliciti Group Ventures Ltd in your preferred currency, with a direct payment link and downloadable PDF."],
  [Megaphone, "Company-wide broadcasts (upgrade)", "Push announcements, training tips and new-command updates to your entire organisation in one click."],
  [Building2, "Full seat control (upgrade)", "Activate or deactivate individual staff or the whole organisation instantly — leavers lose access the moment you say so."],
  [null, "Certified staff training included", "Every seat includes the XpressPrompts™ Starter Training — narrated modules, a quiz and a personalised Certificate of Completion emailed to each staff member as a printable PDF."],
];

const STEPS = [
  ["1", "Request a contract", "Tell us about your company and team size using the form below."],
  ["2", "Sign the NDA online", "We reply within one working day and send a mutual NDA for instant e-signature."],
  ["3", "Receive your proposal & invoice", "A tailored seat-based proposal with a VAT invoice and payment link."],
  ["4", "Scheduled Team onboarding", "Bulk Staff account creation is live with Pro access within days."],
];

const TEAM_SIZES = ["5–10", "11–25", "26–50", "51–100", "101–250", "250+"];

const EMPTY = { company_name: "", contact_person: "", job_title: "", business_email: "", phone: "", country: "", team_size: "", message: "", callback_date: "", callback_time: "", callback_timezone: "", wants_call: false, wants_proposal: false, wants_visit: false };
const inputCls = "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50";
const todayISO = () => new Date().toISOString().slice(0, 10);
const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; } };

export default function EnterprisePage() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const formRef = useRef(null);

  useSeo({
    title: "For Teams & Enterprise — Company-wide AI Prompt Shortcuts",
    description: "Give your whole team Pro access to the Universal Short-Command Prompt Dictionary™. Bulk onboarding, NDA-protected contracts, VAT invoicing and company broadcasts.",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const requiredOk = form.company_name.trim() && form.contact_person.trim() && form.business_email.trim() && form.team_size;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, callback_timezone: form.callback_timezone || browserTz() };
      await axios.post(`${API}/api/enterprise/inquiry`, payload);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAFAF7] min-h-screen" data-testid="enterprise-page">
      {/* HERO */}
      <section className="bg-[#0B0F0D] text-white py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <img src="/assets/xpressprompts-header.webp" alt="" aria-hidden="true" className="w-44 mb-6 hidden" data-testid="enterprise-stamp" />
            <p className="font-display text-[#D4AF37] font-bold text-xs sm:text-sm tracking-[0.16em] uppercase mb-3">For Teams &amp; Enterprise</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              You've given your staff AI.<br />
              <span className="text-[#10B981]">Now give them the words.</span>
            </h1>
            <p className="text-gray-400 mt-5 text-sm sm:text-base leading-relaxed max-w-xl">
              Put the Universal Short-Command Prompt Dictionary™ in front of your entire workforce.
              One contract, every employee on Pro, onboarded in minutes — with NDA-protected terms and
              VAT-ready invoicing handled for you.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button data-testid="enterprise-cta" onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth" })}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold px-8 py-4 rounded-xl hover:brightness-105 active:scale-[0.98] transition-all">
                REQUEST AN ENTERPRISE PROPOSAL
              </button>
            </div>
            <p className="flex items-center gap-2 text-xs text-gray-500 mt-5"><BadgeCheck size={14} className="text-[#10B981]" /> Contracts from Voliciti Group Ventures Ltd · NDA available before any details are shared</p>
          </div>
          <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 shadow-2xl" style={{ paddingBottom: "56.25%" }} data-testid="enterprise-video">
            <iframe
              src="https://www.youtube.com/embed/anT6WzncYfw"
              title="AI Prompt Shortcuts for Teams & Enterprise"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* BENEFITS + FORM */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-center">Everything an enterprise rollout needs</h2>
          <p className="text-slate-500 text-sm sm:text-base text-center mt-3 max-w-2xl mx-auto">Built for HR, L&amp;D and operations teams who want staff using AI properly — without writing essays to it.</p>

          <a
            href="https://www.qualiquiz.app/quiz/discover-your-ai-workforce-readiness-assessment-report"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enterprise-readiness-banner-link"
            className="block max-w-4xl mx-auto mt-8 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl hover:scale-[1.01] transition-all duration-300"
          >
            <img
              src="/assets/ai-workforce-readiness-banner.webp"
              alt="Discover Your AI Workforce Readiness Score — take our free 2-minute assessment and receive a personalised snapshot of where your organisation could improve AI adoption and everyday execution."
              className="w-full h-auto block"
              loading="lazy"
            />
          </a>

          {/* Proposal form — moved to top so decision-makers see it first */}
          <div ref={formRef} className="max-w-3xl mx-auto mt-10">
            {done ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-sm" data-testid="enterprise-success">
                <CheckCircle2 className="mx-auto text-emerald-600" size={44} />
                <h2 className="font-display text-2xl font-extrabold mt-4">Enquiry received</h2>
                <p className="text-slate-500 text-sm mt-3 max-w-md mx-auto">
                  Thank you — we've emailed you a confirmation and will reply within one working day with next steps,
                  starting with a mutual NDA for e-signature.
                </p>
                {form.callback_date && (
                  <div className="mt-6 inline-flex flex-col items-center gap-3">
                    <p className="text-xs text-slate-500">
                      Callback requested for <span className="font-semibold text-slate-700">{form.callback_date}{form.callback_time && ` at ${form.callback_time}`}</span>
                      {form.callback_timezone && <span className="text-slate-400"> ({form.callback_timezone})</span>}
                    </p>
                    <button
                      data-testid="enterprise-save-calendar"
                      onClick={() => {
                        const ics = buildIcs({
                          date: form.callback_date,
                          time: form.callback_time || "09:00",
                          timezone: form.callback_timezone || browserTz(),
                          title: `XpressPrompts callback — ${form.company_name || "your company"}`,
                          description: `Callback with XpressPrompts regarding your enterprise enquiry.\n\nContact: ${form.contact_person}\nEmail: ${form.business_email}${form.phone ? "\nPhone: " + form.phone : ""}${form.message ? "\n\nNotes:\n" + form.message : ""}`,
                          location: "Phone / video call",
                          durationMins: 30,
                        });
                        downloadIcs(ics, `xpressprompts-callback-${form.callback_date}.ics`);
                      }}
                      className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-5 py-3 rounded-xl transition-colors"
                    >
                      <CalendarPlus size={18} /> Save to Calendar (.ics)
                    </button>
                    <p className="text-[11px] text-slate-400 max-w-xs">Opens in Google Calendar, Apple Calendar, Outlook or any calendar app that supports .ics.</p>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm" data-testid="enterprise-form">
                <h3 className="font-display font-extrabold text-xl sm:text-2xl">Request an enterprise proposal</h3>
                <p className="text-slate-500 text-sm mt-2">Tell us about your company — we'll reply within one working day. No obligation.</p>
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  <input required data-testid="ef-company" className={inputCls} placeholder="Company / Organisation name *" value={form.company_name} onChange={set("company_name")} />
                  <input required data-testid="ef-contact" className={inputCls} placeholder="Contact person *" value={form.contact_person} onChange={set("contact_person")} />
                  <input data-testid="ef-job" className={inputCls} placeholder="Job title" value={form.job_title} onChange={set("job_title")} />
                  <input required type="email" data-testid="ef-email" className={inputCls} placeholder="Business email *" value={form.business_email} onChange={set("business_email")} />
                  <input data-testid="ef-phone" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} />
                  <input data-testid="ef-country" className={inputCls} placeholder="Country" value={form.country} onChange={set("country")} />
                </div>
                <div className="mt-4">
                  <label className="text-sm font-bold text-slate-700">Approximate team size *</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {TEAM_SIZES.map((s) => (
                      <button type="button" key={s} data-testid={`ef-size-${s.replace(/[–+]/g, "-")}`}
                        onClick={() => setForm({ ...form, team_size: s })}
                        className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${form.team_size === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-5 rounded-xl bg-amber-50/70 border border-amber-200 p-4" data-testid="ef-requests-block">
                  <label className="text-sm font-bold text-slate-800">What would you like to do next? *</label>
                  <p className="text-xs text-slate-500 mt-1">Tick one or more — this helps us prepare the right person on our side.</p>
                  <div className="grid sm:grid-cols-2 gap-2 mt-3">
                    {[
                      { key: "call",     label: "📞 Book a call",       desc: "A 10-minute intro by phone or video" },
                      { key: "proposal", label: "📄 Request a proposal", desc: "A written proposal tailored to your team" },
                      { key: "visit",    label: "🏢 Book a visit",       desc: "An on-site meeting at your office" },
                    ].map((opt) => {
                      const field = `wants_${opt.key}`;
                      const checked = form[field];
                      return (
                        <label key={opt.key} data-testid={`ef-want-${opt.key}`}
                          className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${checked ? "bg-white border-amber-400 ring-2 ring-amber-200" : "bg-white/60 border-slate-200 hover:border-slate-300"}`}>
                          <input type="checkbox" checked={checked} onChange={(e) => setForm({ ...form, [field]: e.target.checked })}
                            className="mt-0.5 w-4 h-4 accent-amber-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800">{opt.label}</p>
                            <p className="text-[11px] text-slate-500 mt-0.5">{opt.desc}</p>
                          </div>
                        </label>
                      );
                    })}
                    {(() => {
                      const all = form.wants_call && form.wants_proposal && form.wants_visit;
                      return (
                        <label data-testid="ef-want-all"
                          className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${all ? "bg-amber-500/10 border-amber-500 ring-2 ring-amber-300" : "bg-white/60 border-dashed border-slate-300 hover:border-slate-400"}`}>
                          <input type="checkbox" checked={all} onChange={(e) => setForm({ ...form, wants_call: e.target.checked, wants_proposal: e.target.checked, wants_visit: e.target.checked })}
                            className="mt-0.5 w-4 h-4 accent-amber-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-black uppercase tracking-wide text-amber-800">⚡ Book all three</p>
                            <p className="text-[11px] text-amber-700 mt-0.5">Quick-select — call, proposal and on-site visit</p>
                          </div>
                        </label>
                      );
                    })()}
                  </div>
                </div>

                <div className="mt-5 rounded-xl bg-emerald-50/60 border border-emerald-100 p-4" data-testid="ef-callback-block">
                  <label className="text-sm font-bold text-slate-800">Prefer a callback? Pick a date and time (optional)</label>
                  <p className="text-xs text-slate-500 mt-1">We'll call you back at this time. Leave blank and we'll just reply by email.</p>
                  <div className="grid sm:grid-cols-3 gap-3 mt-3">
                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Preferred date</span>
                      <input data-testid="ef-callback-date" type="date" min={todayISO()} value={form.callback_date} onChange={set("callback_date")}
                        className={`${inputCls} mt-1`} />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Preferred time</span>
                      <input data-testid="ef-callback-time" type="time" value={form.callback_time} onChange={set("callback_time")}
                        className={`${inputCls} mt-1`} />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Your time zone</span>
                      <input data-testid="ef-callback-timezone" value={form.callback_timezone || browserTz()} onChange={set("callback_timezone")}
                        placeholder="e.g. Europe/London" className={`${inputCls} mt-1`} />
                    </label>
                  </div>
                </div>

                <textarea data-testid="ef-message" className={`${inputCls} mt-4`} rows={4} maxLength={2000}
                  placeholder="Anything you'd like us to know — goals, departments, timelines…" value={form.message} onChange={set("message")} />
                <button type="submit" disabled={!requiredOk || busy} data-testid="ef-submit"
                  className="mt-6 w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold py-4 rounded-xl hover:brightness-105 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {busy && <Loader2 size={16} className="animate-spin" />} REQUEST PROPOSAL
                </button>
                <p className="text-xs text-slate-400 text-center mt-3">Your details are used only to respond to this enquiry. Contracts are issued by Voliciti Group Ventures Ltd.</p>
              </form>
            )}
          </div>

          {/* How-to video: ready & enable your workforce */}
          <section className="mt-16" data-testid="enterprise-staff-video">
            <h3 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-center">How to ready &amp; enable your staff for AI</h3>
            <p className="text-center text-sm text-slate-600 mt-2 max-w-2xl mx-auto">
              A short walk-through showing how a construction team adopts XpressPrompts day one — pick a shortcut, add rough notes, press go.
            </p>
            <div className="mt-6 max-w-3xl mx-auto rounded-2xl overflow-hidden border border-slate-200 bg-black shadow-sm">
              <video
                controls
                playsInline
                preload="metadata"
                poster="/assets/construction-video-poster.png"
                className="w-full h-auto block"
                data-testid="enterprise-staff-video-player"
              >
                <source src="/videos/construction-staff-ai.mp4" type="video/mp4" />
                Your browser does not support embedded video.
              </video>
            </div>
          </section>

          {/* What's included */}
          <h3 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-center mt-16">What's included</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {BENEFITS.map(([Icon, t, d]) => (
              <div key={t} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-300 hover:shadow-sm transition-all">
                {Icon ? (
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4"><Icon size={20} /></div>
                ) : (
                  <img src="/assets/cert-badge.webp" alt="Earn your Certificate of Completion — XpressPrompts Starter Training"
                    draggable={false} data-testid="enterprise-cert-badge" className="h-12 w-12 mb-3 drop-shadow-md" />
                )}
                <h3 className="font-display font-bold text-slate-900">{t}</h3>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="pb-16 lg:pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto bg-[#0B0F0D] rounded-2xl p-6 sm:p-10 lg:p-12">
          <img src="/assets/enterprise-bridge-gap.webp"
            alt="Bridge the gap between AI enablement and daily execution — by giving your staff access to hundreds of short command prompts"
            data-testid="enterprise-bridge-image" className="w-full max-w-3xl mx-auto mb-10 rounded-xl" />
          <h2 className="font-display text-white text-2xl sm:text-3xl font-extrabold tracking-tight">From enquiry to onboarded — four steps</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {STEPS.map(([n, t, d]) => (
              <div key={n} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="h-9 w-9 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold flex items-center justify-center text-sm">{n}</div>
                <h3 className="font-display font-bold text-white mt-4 text-sm">{t}</h3>
                <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <p className="flex items-center gap-2 text-xs text-gray-400 mt-7"><FileSignature size={14} className="text-[#D4AF37]" /> NDAs are signed online in your browser — no account, printer or scanner required.</p>
        </div>
      </section>

      {/* STAFF ENABLEMENT VIDEO */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Enable your staff with the AI Short-Command Dictionary</h2>
          <p className="text-slate-500 text-sm sm:text-base mt-3 max-w-xl mx-auto">See how quickly your team goes from staring at a blank AI chat box to getting professional results.</p>
          <video
            src="/videos/enterprise-staff-video.mp4"
            controls
            playsInline
            preload="metadata"
            data-testid="enterprise-staff-video"
            className="mt-8 mx-auto w-full max-w-sm rounded-2xl border border-slate-200 shadow-xl bg-black"
          />
        </div>
      </section>
    </div>
  );
}
